import type ConnectedAccountInfo from '../data/schema/ConnectedAccountInfo';
import type SubstrateSearchRequest from '../data/schema/SubstrateSearchRequest';
import type SubstrateSearchScenario from '../data/schema/SubstrateSearchScenario';
import type { SubstrateSearchRequestV3 } from '../data/schema/SubstrateSearchRequestV3';
import buildQueryParams from '../helpers/buildQueryParams';
import { getLocalTime } from '../helpers/getLocalTime';
import getSubstrateSearchEndpoint from '../helpers/getSubstrateSearchEndpoint';
import type { ShadowRequestOptions } from '../data/schema/ShadowRequestOptions';
import {
    SHADOW_REQUEST_HEADER,
    SHADOW_SCENARIO_TAG_SUFFIX,
} from '../data/schema/ShadowRequestOptions';
import { getGuid } from 'owa-guid';
import { makePostRequest } from 'owa-ows-gateway';
import { createDefaultHeadersSync } from 'owa-service/lib/createDefaultHeader';
import { getCanaryHeaders } from 'owa-service/lib/setCanaryHeader';
import { getCurrentCulture } from 'owa-localize';
import { trace } from 'owa-trace';
import getUrlWithAddedQueryParameters from 'owa-url/lib/getUrlWithAddedQueryParameters';
import type { MailboxInfo } from 'owa-client-types';
import type SubstrateSearchEndpointTarget from '../data/schema/SubstrateSearchEndpointTarget';

/**
 * This function allows caller to hit 3S service directly by providing a
 * SubstrateSearchRequest object. Instead of receiving a SubstrateSearchQueryResponse
 * object back directly, the caller will be required to check the response
 * and the response status, and act accordingly (i.e. extracting the results
 * from the response body or handling an error from the API).
 *
 * @param substrateSearchRequest The request object for 3S query request
 * @param searchSessionId The ID identifying the user's current search session (client-session-id header value)
 * @param xClientFlightsHeaderValue Flight names to pass to the 3S service to enable server-side features
 * @param connectedAccountInfo ConnectedAccountInfo object in order to stamp the anchorMailbox and authorization info
 * for searching in multi account scenarios.
 * @param defaultQueryParams Default query params to add to the 3S request URL.
 * @param shadowOptions When set, the request is a shadow (comparison-only) request: it targets the
 * requested backend variant explicitly and is marked so it can be excluded from serving telemetry.
 * @returns [response, headers] - "response" will be the actual respons from
 * the 3S call. "headers" will be the custom headers generated and passed with
 * the response (required for instrumentation purposes). returns ["error"] in case of web exception
 */
export default function substrateSearchService(
    substrateSearchRequest: SubstrateSearchRequest | SubstrateSearchRequestV3,
    searchSessionId?: string,
    xClientFlightsHeaderValue?: string,
    connectedAccountInfo?: ConnectedAccountInfo[],
    customHeaders?: {
        [key: string]: string;
    },
    substrateApiVersion?: number,
    mailboxInfo?: MailboxInfo,
    substrateSearchScenario?: SubstrateSearchScenario,
    defaultQueryParams?: {
        [key: string]: string;
    },
    endpointTarget?: SubstrateSearchEndpointTarget,
    shadowOptions?: ShadowRequestOptions
): Promise<any[]> {
    const requestHeaders = createSubstrateSearchQueryHeaders(
        substrateSearchRequest,
        searchSessionId,
        xClientFlightsHeaderValue ?? '',
        mailboxInfo ? [] : connectedAccountInfo ?? [],
        customHeaders,
        substrateApiVersion,
        substrateSearchScenario,
        shadowOptions
    );
    /* eslint-disable-next-line owa-custom-rules/require-mailboxInfoInOwsRequest  -- (https://aka.ms/OWALintWiki)
     * MailboxInfo must be providded to OWS calls, see https://aka.ms/multiaccountlinter
     *	> All OWS calls must pass in a MailboxInfo or OwsRequestOptions obtained via getOwsMailboxRequestOptions. */
    return makePostRequest(
        getUrlWithAddedQueryParameters(
            getSubstrateSearchEndpoint(
                'query',
                substrateApiVersion,
                substrateSearchScenario,
                mailboxInfo,
                endpointTarget
            ),
            buildQueryParams(defaultQueryParams)
        ),
        substrateSearchRequest /* requestObject */,
        undefined /* correlationId (client-request-id set in custom headers) */,
        true /* returnFullResponse */,
        requestHeaders /* customHeaders */,
        undefined /* throwServiceError */,
        undefined /* sendPayloadAsBody */,
        false /* includeCredentials */,
        'substrateSearchService' /* actionName */,
        undefined /* RequestDatapointOptions */,
        mailboxInfo
    )
        .then(response => {
            return [response, requestHeaders];
        })
        .catch((error: any) => {
            trace.warn('substrateSearchService failed');
            return [error];
        });
}

function createSubstrateSearchQueryHeaders(
    request: SubstrateSearchRequest | SubstrateSearchRequestV3,
    searchSessionId: string | undefined,
    xClientFlightsHeaders: string,
    connectedAccountInfo: ConnectedAccountInfo[],
    customHeaders?: {
        [key: string]: string;
    },
    substrateApiVersion?: number,
    substrateSearchScenario?: SubstrateSearchScenario,
    shadowOptions?: ShadowRequestOptions
) {
    // This function should call createDefaultHeaders. But this function is an async function
    // which will slow down the performance of search. So we will call createDefaultHeadersWithoutAuth
    // and try to use the last auth token fetched. If we are in native-host or opx and the token is expired
    // this scenario will not work as we will not try to refetch the auth token.
    // WI OutlookWeb 102560
    const headers = { ...createDefaultHeadersSync(), ...getCanaryHeaders() };

    // Add custom headers.
    if (searchSessionId) {
        headers['client-session-id'] = searchSessionId;
    }

    if (!headers['client-request-id']) {
        headers['client-request-id'] = getGuid();
    }
    headers['x-client-localtime'] = getLocalTime();
    headers['X-Search-Griffin-Version'] = 'GWSv2';
    headers['Accept-Language'] = getCurrentCulture();

    if (connectedAccountInfo && connectedAccountInfo.length > 0) {
        headers['x-anchormailbox'] = connectedAccountInfo[0].mailboxSmtpAddress;
        headers['authorization'] = connectedAccountInfo[0].tokenString;
    }

    /**
     * These are flights required by the service to enable features on their end.
     * If there are no flights passed in, don't set the header. Edge v38 doesn't
     * implement the fetch API correctly and requests fail if any header is set
     * to any empty string.
     */
    if (xClientFlightsHeaders) {
        headers['X-Client-Flights'] = xClientFlightsHeaders;
    }

    if (substrateApiVersion === 3) {
        const scenarioName =
            substrateSearchScenario ??
            (
                request as {
                    Scenario?: {
                        Name?: SubstrateSearchScenario;
                    };
                }
            ).Scenario?.Name;
        if (scenarioName) {
            headers['x-scenario'] = scenarioName;
        }
    }

    // an instrumentation tag that allows us to differentiate the various query scenarios
    const scenarioTagKey = 'scenariotag';
    headers[scenarioTagKey] = buildScenarioTag(request);

    if (customHeaders) {
        Object.keys(customHeaders).forEach(k => {
            headers[k] = customHeaders[k];
        });
    }

    // Shadow requests must never be mistaken for the request that served the user: they get their
    // own correlation id (so the served request keeps a unique client-request-id) and are marked so
    // both the service and client-side dashboards can exclude comparison-only load. This is applied
    // after customHeaders so a caller-supplied client-request-id cannot leak onto the shadow.
    if (shadowOptions?.isShadow) {
        headers['client-request-id'] = getGuid();
        headers[SHADOW_REQUEST_HEADER] = '1';
        headers[scenarioTagKey] = `${headers[scenarioTagKey]}_${SHADOW_SCENARIO_TAG_SUFFIX}`;
    }

    return headers;
}

function buildScenarioTag(request: SubstrateSearchRequest | SubstrateSearchRequestV3) {
    const hasMultipleEntityRequests = request.EntityRequests.length > 1;
    const entityRequest = request.EntityRequests[0];
    const scenarioTags = [];
    if (hasMultipleEntityRequests) {
        scenarioTags.push('ErrMER');
    }

    const requestOptionsFrom =
        'RequestOptions' in request ? request.RequestOptions?.From : undefined;
    const from = entityRequest.From ?? requestOptionsFrom;
    if (from == 0) {
        scenarioTags.push('1stPg');
    }

    const shortItemType = entityTypeShortName[entityRequest.EntityType];
    scenarioTags.push(shortItemType || 'Unknown');

    return scenarioTags.join('_');
}

const entityTypeShortName: any = {
    Event: 'ev',
    Conversation: 'cv',
    Email: 'em',
    Message: 'mg',
    EmailConversation: 'ec',
    MailItems: 'mi',
    CalendarItems: 'ci',
    MailConversations: 'mc',
    MessageItems: 'gi',
};
